import { memo, useEffect, useRef, useState } from "react";
import { Grid2, TextField } from "@mui/material";
import { useParams } from "react-router-dom";
import ChatInput from "./ChatInput";
import ChatMessageContainer from "./ChatMessageContainer";
import SelectionBoard from "./SelectionBoard";
import { useViewSlice } from "@store/slices/view";
import { useQuery } from "@apollo/client";
import { Chat as ChatType, Query } from "@/app/graphql/codegen";
import { FETCH_MESSAGES_FOR_CHAT } from "@chat/graphql/queries/fetchMessagesForChat";
import { FETCH_CHAT } from "@chat/graphql/queries/fetchChat";
import { useChatMessages } from "@chat/hooks/useChatMessages";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import Mark from "mark.js";
import { availableViewportHeight } from "@/domains/core/layout/AppLayout";

/**
 * TODO @lwun major refactor
 */
const Chat = memo(() => {
    const { id: chatId } = useParams<{ id: string }>();
    const { showExtendedView } = useViewSlice();

    const [searchValue, setSearchValue] = useState<string>("");
    const chatContentRef = useRef<HTMLDivElement | null>(null);
    const markInstance = useRef<Mark | null>(null);

    useEffect(() => {
        if (chatContentRef.current && !markInstance.current) {
            markInstance.current = new Mark(chatContentRef.current);
        }
    }, []);

    useEffect(() => {
        if (markInstance.current) {
            // Erst alle Markierungen löschen
            markInstance.current.unmark();

            // Nur markieren, wenn searchValue nicht leer ist
            if (searchValue.trim()) {
                markInstance.current.mark(searchValue, {
                    accuracy: "partially",
                    separateWordSearch: true,
                    className: "highlighted-text",
                    acrossElements: true,
                    // done: function (count) {
                    //     console.log(`${count} Treffer gefunden für '${searchValue}'`);
                    // },
                });
            }
        }
    }, [searchValue]);

    const { data: chatData, refetch: refetchChat } = useQuery<Pick<Query, "chat">>(FETCH_CHAT, {
        variables: { id: chatId || "" },
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
        errorPolicy: "all",
        pollInterval: 3000, // Keep meta information (e.g. isGenerating) in sync
        onError: (error) => {
            console.error("Error fetching chat data:", error);
        },
    });

    const { refetch: refetchUserProfile } = useQuery<Pick<Query, "userProfile">>(FETCH_USER_PROFILE);

    const isGenerating = chatData?.chat.isGenerating ?? false;

    // Adjust polling: faster (1 s) while generation is running, otherwise 3 s
    const pollIntervalMs = isGenerating ? 1000 : 3000;

    const { messages, loading, refetch } = useChatMessages(chatId, undefined, pollIntervalMs);

    const refetchMessages = () => {
        Promise.all([
            refetch(),
            refetchChat(),
            refetchUserProfile(),
        ]).catch((error) => {
            console.error("Error refetching chat data:", error);
            setTimeout(() => {
                refetchChat().catch(() => {});
            }, 1000);
        });
    };

    // Disable global page scroll while Chat is mounted
    useEffect(() => {
        const htmlEl = document.documentElement;
        const bodyEl = document.body;
        const rootEl = document.getElementById("root");

        const prevHtmlOverflow = htmlEl.style.overflow;
        const prevBodyOverflow = bodyEl.style.overflow;
        const prevRootOverflow = rootEl?.style.overflow || "";
        const prevRootHeight = rootEl?.style.height || "";

        htmlEl.style.overflow = "hidden";
        bodyEl.style.overflow = "hidden";
        if (rootEl) {
            rootEl.style.overflow = "hidden";
            rootEl.style.height = "100vh";
        }

        return () => {
            htmlEl.style.overflow = prevHtmlOverflow;
            bodyEl.style.overflow = prevBodyOverflow;
            if (rootEl) {
                rootEl.style.overflow = prevRootOverflow;
                rootEl.style.height = prevRootHeight;
            }
        };
    }, []);

    return (
        <Grid2
            container
            size={12}
            direction={"column"}
            sx={{
                border: `${showExtendedView ? 1 : 0}px solid purple`,
                height: availableViewportHeight,
                minHeight: 0,
                overflow: "hidden",
            }}
        >
            {chatId && (
                <Grid2 size={12} mb={1}>
                    <SelectionBoard chatId={chatId} />
                </Grid2>
            )}

            {/* Messages - Scrollable Area */}
            <Grid2
                ref={chatContentRef}
                container
                size={"grow"}
                sx={{
                    flex: "1 1 0%",
                    height: 0,
                    minHeight: 0,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    WebkitOverflowScrolling: "touch",
                    "&::-webkit-scrollbar": { display: "none" }, // Für Webkit
                    scrollbarWidth: "none", // Für Firefox
                    border: `${showExtendedView ? 1 : 0}px solid blue`,
                }}
            >
                <ChatMessageContainer chatId={chatId} messages={messages} refetchMessages={refetchMessages} isGenerating={isGenerating} />
            </Grid2>
            {/* Message Input - Fixed at bottom */}
            <Grid2 mt={2}>
                <ChatInput disabled={loading} id={chatId || ""} isGenerating={isGenerating} searchValue={searchValue} setSearchValue={setSearchValue} />
            </Grid2>
        </Grid2>
    );
});

export default Chat;
